import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  styled,
  Paper,
  Fade,
  Slide,
  Zoom,
  Stack
} from '@mui/material';

import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';

import {
  createLoanApplicationProposal,
  updateProposalDetails,
  getApplicationDetails,
  getS3UrlByKeyBase64,
  getOnePagerByAppNumber,
  generateCMAByApplicationNumber,
  notify
} from '../../API/API';
import ReactLenderSelectionTable from '../../components/proposals/LenderSelectionTable';
import FileUploadZone from '../../components/proposals/FileUploadZone';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'transparent',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4)
  }
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  maxWidth: '1400px !important',
  padding: 0,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 1)
  }
}));

const MainCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: '32px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  overflow: 'visible',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 32px 80px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-4px)'
  }
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(6),
  '&:last-child': {
    paddingBottom: theme.spacing(6)
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8),
    '&:last-child': {
      paddingBottom: theme.spacing(8)
    }
  }
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    '&::before': {
      opacity: 1,
      transform: 'scaleX(1)'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    opacity: 0,
    transform: 'scaleX(0)',
    transition: 'all 0.4s ease'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(4, 5),
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  '& .MuiSvgIcon-root': {
    color: '#667eea',
    fontSize: '1.5rem',
    padding: theme.spacing(1),
    borderRadius: '12px',
    background: 'rgba(102, 126, 234, 0.1)'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.25rem',
  fontWeight: 700,
  margin: 0,
  letterSpacing: '-0.02em',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent'
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  lineHeight: 1.5,
  opacity: 0.8
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      borderColor: 'rgba(102, 126, 234, 0.3)',
      boxShadow: '0 4px 16px rgba(102, 126, 234, 0.1)'
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      borderColor: '#667eea',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.15)'
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  }
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '16px',
  padding: '14px 32px',
  fontSize: '0.875rem',
  fontWeight: 700,
  textTransform: 'none',
  letterSpacing: '0.02em',
  background: variant === 'contained'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'transparent',
  border: variant === 'outlined' ? '2px solid rgba(102, 126, 234, 0.3)' : 'none',
  color: variant === 'contained' ? '#FFFFFF' : '#667eea',
  boxShadow: variant === 'contained' ? '0 8px 24px rgba(102, 126, 234, 0.3)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? '0 12px 32px rgba(102, 126, 234, 0.4)' : '0 4px 16px rgba(102, 126, 234, 0.1)',
    background: variant === 'contained'
      ? 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
      : 'rgba(102, 126, 234, 0.05)'
  }
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(5),
  padding: theme.spacing(3, 4),
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  backdropFilter: 'blur(10px)'
}));

const ProgressStep = styled(Box)(({ theme, active, completed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  borderRadius: '12px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: completed ? '#10b981' : active ? '#667eea' : theme.palette.text.secondary,
  backgroundColor: completed ? 'rgba(16, 185, 129, 0.1)' : active ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
  transition: 'all 0.3s ease',
  opacity: completed || active ? 1 : 0.6,
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  }
}));

const ConsultantCreateProposal = () => {
  const navigate = useNavigate();
  const { applicationId: applicationIdParam, proposalId: proposalIdParam } = useParams();
  const applicationId = applicationIdParam || proposalIdParam;
  const location = useLocation();
  const { proposalData, isUpdate, lenders: preselectedLenders } = location.state || {};

  const [formData, setFormData] = useState({
    applicationId: applicationId || '',
    applicationNumber: '',
    user_id: '',
    lenderId: [],
    borrowerId: '',
    borrowerType: '',
    proposalBrief: ''
  });

  const [files, setFiles] = useState({
    cma: null,
    onePager: null
  });

  const [loading, setLoading] = useState({
    page: true,
    submit: false,
    onePager: false,
    cma: false
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [applicationUserId, setApplicationUserId] = useState('');

  const [currentStep, setCurrentStep] = useState(0);

  const updatedSteps = useMemo(() => {
    const steps = [
      { label: 'Application', completed: false },
      { label: 'Documents', completed: false },
      { label: 'Brief', completed: false },
      { label: 'Lenders', completed: false }
    ];

    return steps.map((step, index) => {
      let completed = false;

      switch (index) {
        case 0:
          completed = formData.applicationId && formData.applicationNumber;
          break;
        case 1:
          completed = files.cma || files.onePager;
          break;
        case 2:
          completed = formData.proposalBrief && formData.proposalBrief.trim().length > 0;
          break;
        case 3:
          completed = formData.lenderId && formData.lenderId.length > 0;
          break;
        default:
          completed = false;
      }

      return { ...step, completed };
    });
  }, [formData, files]);

  useEffect(() => {
    const activeStep = updatedSteps.findIndex(step => !step.completed);
    if (activeStep !== -1) {
      setCurrentStep(activeStep);
    } else {
      setCurrentStep(updatedSteps.length - 1);
    }
  }, [updatedSteps]);

  const user = useSelector((state) => state.user?.user);

  const borrowerType = String(formData?.borrowerType || '').toLowerCase().trim();
  const isIndividualBorrower = borrowerType === 'individual';

  useEffect(() => {
    if (user && user.UserType !== 'consultant' && user.UserType !== 'Consultant') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(prev => ({ ...prev, page: true }));

        if (user?.ID) {
          setFormData(prev => ({ ...prev, user_id: user.ID }));
        }

        const apiFiles = {};

        if (applicationId) {
          const response = await getApplicationDetails(applicationId);
          if (response?.result) {
            const result = response.result;
            setFormData(prev => ({
              ...prev,
              applicationNumber: result.applicationNumber || '',
              borrowerId: result.userId?.id || '',
              borrowerType: result.borrowerType || result.BorrowerType || ''
            }));
            setApplicationUserId(result.userId?.id || '');

            ['cma', 'onePager'].forEach((type) => {
              const path = result[type];
              if (path) {
                const file = new File([], type === 'cma' ? 'CMA Report' : 'One Pager');
                file.existingUrl = path;
                file.s3Key = btoa(
                  encodeURIComponent(path.trim()).replace(/%([0-9A-F]{2})/g, (_, hex) =>
                    String.fromCharCode(parseInt(hex, 16))
                  )
                );
                apiFiles[type] = file;
              }
            });
          }
        }

        if (Object.keys(apiFiles).length > 0) {
          setFiles((prev) => ({ ...prev, ...apiFiles }));
        }

        if (isUpdate) {
          setFormData(prev => ({
            ...prev,
            proposalBrief: proposalData?.proposalBrief || prev.proposalBrief || '',
            lenderId: Array.isArray(preselectedLenders) ? preselectedLenders : (prev.lenderId || [])
          }));

          if (proposalData) {
            const existingFiles = {};

            if (proposalData.cma && !apiFiles.cma) {
              const cmaFile = new File([], 'CMA Report');
              cmaFile.existingUrl = proposalData.cma;
              existingFiles.cma = cmaFile;
            }

            if (proposalData.onePager && !apiFiles.onePager) {
              const onePagerFile = new File([], 'One Pager');
              onePagerFile.existingUrl = proposalData.onePager;
              existingFiles.onePager = onePagerFile;
            }

            if (Object.keys(existingFiles).length > 0) {
              setFiles(prev => ({ ...prev, ...existingFiles }));
            }
          }
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load initial data',
          severity: 'error'
        });
      } finally {
        setLoading(prev => ({ ...prev, page: false }));
      }
    };

    initializeData();
  }, [applicationId, user, isUpdate, preselectedLenders, proposalData]);

  const handleFileChange = (type, file) => {
    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const extractDownloadUrl = (response) => {
    return [
      response?.result?.url,
      response?.url,
      response?.presignedUrl,
      response?.result,
      response?.data
    ].find(value => typeof value === 'string' && value.trim().length > 0);
  };

  const notifyBorrowerForGeneratedDocs = async (docType) => {
    const borrowerId = parseInt(formData.borrowerId || applicationUserId, 10);
    if (!borrowerId) return;

    const appNumber = String(formData.applicationNumber || '').trim();

    const titles = {
      CMA: 'CMA Report Generated',
      ONE_PAGER: 'One Pager Generated'
    };

    const base =
      docType === 'CMA'
        ? 'CMA report'
        : docType === 'ONE_PAGER'
          ? 'One Pager document'
          : 'Document';

    const title = titles[docType] || 'Document Generated';
    const content = appNumber
      ? `Your ${base} for Application Number ${appNumber} has been generated and is now available in your submitted application.`
      : `Your ${base} has been generated and is now available in your submitted application.`;

    const payload = {
      userId: borrowerId,
      title,
      content,
      createdByRole: String(user?.UserType || 'Consultant'),
      type: 'documentGeneration',
      action: 'View Application',
      actionUrl: '/application-history',
      unread: true
    };

    try {
      await notify(payload);
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to send document generation notification. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleFileDownload = async (fileType) => {
    const fileToDownload = files[fileType];
    if (!fileToDownload) {
      toast.error('Document not available for download.');
      return;
    }

    try {
      let downloadUrl;

      let keyBase64 = fileToDownload.s3Key;

      if (!keyBase64 && fileToDownload.existingUrl?.trim()) {
        const raw = fileToDownload.existingUrl.trim();
        keyBase64 = btoa(
          encodeURIComponent(raw).replace(/%([0-9A-F]{2})/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
          )
        );
      }

      if (keyBase64) {
        const response = await getS3UrlByKeyBase64(keyBase64);
        downloadUrl = extractDownloadUrl(response);

        if (downloadUrl) {
          fileToDownload.s3Url = downloadUrl;
          setFiles(prev => ({
            ...prev,
            [fileType]: fileToDownload
          }));
        }
      }

      if (!downloadUrl && fileToDownload instanceof File) {
        downloadUrl = URL.createObjectURL(fileToDownload);
      }

      if (!downloadUrl) {
        downloadUrl = fileToDownload.s3Url || fileToDownload.existingUrl;
      }

      if (!downloadUrl) {
        toast.error('Unable to fetch download link.');
        return;
      }

      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error fetching download URL:', error);
      toast.error('Failed to fetch download link.');
    }
  };

  const handleGenerateOnePager = async () => {
    if (!formData.applicationId) {
      setSnackbar({
        open: true,
        message: 'Application ID is required to generate one pager.',
        severity: 'warning'
      });
      return;
    }

    setLoading(prev => ({ ...prev, onePager: true }));

    try {
      const applicationInfo = {
        applicationId: formData.applicationId,
        applicationNumber: formData.applicationNumber
      };

      const response = await getOnePagerByAppNumber(applicationInfo, { outputType: "word" });
      const fileName = 'One Pager.docx';
      const onePagerFile = new File([new Blob()], fileName, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      onePagerFile.generatedFromButton = true;

      const headersSnapshot = response?.headers?.toJSON?.() ?? response?.headers ?? {};
      const getHeaderValue = (headers, snapshot, name) => {
        const lowerName = name.toLowerCase();
        return headers?.get?.(name) ?? headers?.[name] ??
          [...(headers?.entries?.() ?? [])].find(([k]) => k?.toLowerCase() === lowerName)?.[1] ??
          Object.entries(snapshot ?? {}).find(([k]) => k?.toLowerCase() === lowerName)?.[1];
      };

      const s3PathHeader = getHeaderValue(response?.headers, headersSnapshot, 'x-s3-url');

      if (s3PathHeader?.trim()) {
        const encodedKey = btoa(
          encodeURIComponent(s3PathHeader.trim()).replace(/%([0-9A-F]{2})/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
          )
        );
        onePagerFile.s3Key = encodedKey;
      }

      handleFileChange('onePager', onePagerFile);

      setSnackbar({
        open: true,
        message: 'One pager generated successfully.',
        severity: 'success'
      });

      if (onePagerFile.generatedFromButton) {
        await notifyBorrowerForGeneratedDocs('ONE_PAGER');
      }
    } catch (error) {
      console.error('Error generating one pager:', error);

      const getErrorMessage = async () => {
        const { message } = error ?? {};
        const defaultMessage = 'Failed to generate one pager.';

        const responseData = error?.response?.data;
        if (!responseData) return message ?? defaultMessage;

        try {
          const parsedMessage = responseData instanceof Blob
            ? JSON.parse(await responseData.text())?.message
            : responseData.message;

          return parsedMessage ?? message ?? defaultMessage;
        } catch (parseError) {
          toast.warn(`Failed to parse one pager error response: ${parseError}`);
          return message ?? defaultMessage;
        }
      };

      const derivedMessage = await getErrorMessage();
      const isRetrievalPending = derivedMessage.toLowerCase().includes('retrival pending');

      setSnackbar({
        open: true,
        message: derivedMessage,
        severity: isRetrievalPending ? 'info' : 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, onePager: false }));
    }
  };

  const handleGenerateCMA = async () => {
    if (!formData.applicationId) {
      setSnackbar({
        open: true,
        message: 'Application ID is required to generate CMA report.',
        severity: 'warning'
      });
      return;
    }

    setLoading(prev => ({ ...prev, cma: true }));

    try {
      const response = await generateCMAByApplicationNumber({
        applicationId: formData.applicationId,
        applicationNumber: formData.applicationNumber
      });
      const blobData = (response?.data || response) instanceof Blob
        ? response?.data || response
        : new Blob([response?.data || response], { type: 'application/pdf' });

      if (!blobData) {
        throw new Error('No data received from CMA service');
      }

      const fileName = 'CMA Report.pdf';
      const cmaFile = new File([blobData], fileName, { type: 'application/pdf' });
      cmaFile.generatedFromButton = true;

      const headersSnapshot = response?.headers?.toJSON?.() ?? response?.headers ?? {};
      const getHeaderValue = (headers, snapshot, name) => {
        const lowerName = name.toLowerCase();
        return headers?.get?.(name) ?? headers?.[name] ??
          [...(headers?.entries?.() ?? [])].find(([k]) => k?.toLowerCase() === lowerName)?.[1] ??
          Object.entries(snapshot ?? {}).find(([k]) => k?.toLowerCase() === lowerName)?.[1];
      };

      const s3PathHeader = getHeaderValue(response?.headers, headersSnapshot, 'x-s3-url');
      if (s3PathHeader?.trim()) {
        const encodedKey = btoa(
          encodeURIComponent(s3PathHeader.trim()).replace(/%([0-9A-F]{2})/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
          )
        );
        cmaFile.s3Key = encodedKey;
      }

      handleFileChange('cma', cmaFile);

      setSnackbar({
        open: true,
        message: 'CMA report generated successfully.',
        severity: 'success'
      });

      if (cmaFile.generatedFromButton) {
        await notifyBorrowerForGeneratedDocs('CMA');
      }
    } catch (error) {
      toast.error(error?.message || 'Error generating CMA report.');
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to generate CMA report.',
        severity: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, cma: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, submit: true }));

    try {
      let response;
      if (isUpdate) {
        const updateFiles = {};
        if (files.cma && !files.cma.existingUrl) updateFiles.cma = files.cma;
        if (files.onePager && !files.onePager.existingUrl) updateFiles.onePager = files.onePager;

        response = await updateProposalDetails({
          id: proposalData?.id,
          applicationId: parseInt(formData.applicationId, 10) || undefined,
          applicationNumber: formData.applicationNumber,
          userId: parseInt(applicationUserId, 10) || undefined,
          borrowerId: parseInt(formData.borrowerId, 10) || undefined,
          lenderId: formData.lenderId,
          proposalBrief: formData.proposalBrief,
          files: updateFiles
        });
      } else {
        response = await createLoanApplicationProposal({
          ...formData,
          files
        });
      }

      if (response?.message === "A proposal has already been created for this application") {
        setSnackbar({
          open: true,
          message: response.message,
          severity: 'info'
        });
        setTimeout(() => {
          navigate('/proposals');
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: isUpdate ? 'Proposal updated successfully!' : 'Proposal created successfully!',
          severity: 'success'
        });
        setTimeout(() => {
          navigate('/proposals');
        }, 2000);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || (isUpdate ? 'Failed to update proposal' : 'Failed to create proposal'),
        severity: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  const handleBack = () => navigate(-1);

  if (loading.page) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#FFFFFF' }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Box sx={{ mb: 4 }}>
          <ActionButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Proposals
          </ActionButton>
        </Box>

        <Typography
          variant="h3"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            mb: 2
          }}
        >
          Create Loan Proposal
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 4,
            fontSize: '1.1rem'
          }}
        >
          Create a new loan proposal and select lenders to send it to.
        </Typography>

        <ProgressContainer>
          {updatedSteps.map((step, index) => (
            <ProgressStep
              key={index}
              active={index === currentStep}
              completed={step.completed}
            >
              {step.completed ? (
                <CheckCircleIcon sx={{ fontSize: '1.5rem' }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: '1.5rem' }} />
              )}
              {step.label}
            </ProgressStep>
          ))}
        </ProgressContainer>

        <MainCard>
          <StyledCardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>

                <Fade in timeout={300}>
                  <SectionCard sx={{ width: '100%' }}>
                    <SectionHeader>
                      <AssignmentIcon />
                      <Box>
                        <SectionTitle>Application Details</SectionTitle>
                        <SectionDescription>
                          Basic information about the loan application
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Application ID"
                            name="applicationId"
                            value={formData.applicationId}
                            disabled
                            InputProps={{
                              startAdornment: (
                                <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                                  #
                                </Box>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <StyledTextField
                            fullWidth
                            label="Application Number"
                            name="applicationNumber"
                            value={formData.applicationNumber}
                            disabled
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </SectionCard>
                </Fade>

                <Slide direction="up" in timeout={400}>
                  <SectionCard sx={{ width: '100%' }}>
                    <SectionHeader>
                      <UploadFileIcon />
                      <Box>
                        <SectionTitle>Upload Documents</SectionTitle>
                        <SectionDescription>
                          Upload required documents for the loan proposal
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <ActionButton
                          variant="outlined"
                          onClick={handleGenerateCMA}
                          disabled={loading.cma || isIndividualBorrower}
                          startIcon={
                            loading.cma
                              ? <CircularProgress size={20} sx={{ color: '#667eea' }} />
                              : <DescriptionIcon />
                          }
                        >
                          {loading.cma ? 'Generating...' : 'Generate CMA Report'}
                        </ActionButton>
                        <ActionButton
                          variant="outlined"
                          onClick={handleGenerateOnePager}
                          disabled={loading.onePager || isIndividualBorrower}
                          startIcon={
                            loading.onePager
                              ? <CircularProgress size={20} sx={{ color: '#667eea' }} />
                              : <DescriptionIcon />
                          }
                        >
                          {loading.onePager ? 'Generating...' : 'Generate One Pager'}
                        </ActionButton>
                      </Box>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <FileUploadZone
                            title="CMA Document"
                            description="Upload your CMA document (PDF, DOC, DOCX, XLS, XLSX)"
                            icon={<DescriptionIcon />}
                            file={files.cma}
                            onFileChange={(file) => handleFileChange('cma', file)}
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            onDownload={() => handleFileDownload('cma')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FileUploadZone
                            title="One Pager"
                            description="Upload your one pager document (PDF, DOC, DOCX)"
                            icon={<DescriptionIcon />}
                            file={files.onePager}
                            onFileChange={(file) => handleFileChange('onePager', file)}
                            accept=".pdf,.doc,.docx"
                            onDownload={() => handleFileDownload('onePager')}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </SectionCard>
                </Slide>

                <Slide direction="up" in timeout={500}>
                  <SectionCard sx={{ width: '100%' }}>
                    <SectionHeader>
                      <EditIcon />
                      <Box>
                        <SectionTitle>Proposal Brief</SectionTitle>
                        <SectionDescription>
                          Provide a detailed summary of the loan proposal
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <StyledTextField
                        fullWidth
                        label="Proposal Brief"
                        name="proposalBrief"
                        value={formData.proposalBrief}
                        onChange={e => setFormData(prev => ({ ...prev, proposalBrief: e.target.value }))}
                        multiline
                        minRows={5}
                        maxRows={10}
                        placeholder="Enter a detailed summary of the loan proposal including purpose, amount, and key highlights..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '20px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: '#FFFFFF',
                              borderColor: 'rgba(102, 126, 234, 0.3)',
                              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.1)'
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#FFFFFF',
                              borderColor: '#667eea',
                              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.15)'
                            }
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 600,
                            color: 'text.secondary',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Box>
                  </SectionCard>
                </Slide>

                <Slide direction="up" in timeout={550}>
                  <SectionCard sx={{ width: '100%' }}>
                    <SectionHeader>
                      <BusinessIcon />
                      <Box>
                        <SectionTitle>Select Lenders</SectionTitle>
                        <SectionDescription>
                          Choose lenders to send this proposal to
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <ReactLenderSelectionTable
                        selectedLenders={formData.lenderId || []}
                        onSelectionChange={(newSelection) =>
                          setFormData(prev => ({ ...prev, lenderId: newSelection }))
                        }
                      />
                    </Box>
                  </SectionCard>
                </Slide>

                <Zoom in timeout={700}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'flex-end',
                      pt: 4,
                      borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                    }}
                  >
                    <ActionButton
                      variant="outlined"
                      onClick={handleBack}
                      disabled={loading.submit}
                      sx={{ minWidth: 140 }}
                    >
                      Cancel
                    </ActionButton>
                    <ActionButton
                      variant="contained"
                      type="submit"
                      disabled={loading.submit}
                      startIcon={loading.submit ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <ArrowForwardIcon />}
                      sx={{ minWidth: 180 }}
                    >
                      {loading.submit ? (isUpdate ? 'Updating...' : 'Creating...') : (isUpdate ? 'Update Proposal' : 'Create Proposal')}
                    </ActionButton>
                  </Box>
                </Zoom>
              </Stack>
            </form>
          </StyledCardContent>
        </MainCard>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ConsultantCreateProposal;
